/**
 * Admin Onboarding — Mandatory Password Change
 * =============================================
 *
 * TREATX-ADMIN-ONBOARDING-01 · frontend
 *
 * Rendered when the caller carries `must_change_password=true`. The
 * normal admin workspace is blocked until this form succeeds. Uses
 * the canonical `POST /api/auth/change-password` endpoint; no new
 * password service is created here.
 *
 * After a successful change the temporary password stops
 * authenticating (backend clears the flag atomically), and this
 * page routes to the email-verification step (or straight to the
 * admin workspace if email is already verified).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, KeyRound, Loader2 } from 'lucide-react';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function AdminOnboardingChangePassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/onboarding/status');
        setStatus(data);
        if (!data?.must_change_password) {
          // Skip forward: password already replaced.
          if (data?.email_verified) {
            navigate('/treatx-admin/dashboard', { replace: true });
          } else {
            navigate('/admin/onboarding/verify-required', { replace: true });
          }
        }
      } catch (e) {
        setErr('Could not verify onboarding state. Please log in again.');
      }
      setStatusLoading(false);
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (!/[A-Za-z]/.test(next) || !/[0-9]/.test(next)) {
      setErr('New password must include letters AND digits.');
      return;
    }
    if (next !== confirm) {
      setErr('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setErr('New password must be different from the temporary password.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password updated', {
        description: 'Your temporary password is no longer valid.',
      });
      // Backend invalidates the session on password change — re-login.
      navigate('/login?after=admin-onboarding-verify', { replace: true });
    } catch (ex) {
      const detail = ex?.response?.data?.detail
        || ex?.response?.data?.message
        || ex?.message
        || 'Password change failed.';
      setErr(String(detail));
    }
    setBusy(false);
  };

  if (statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking onboarding state…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
         data-testid="admin-onboarding-change-password">
      <Card className="w-full max-w-md shadow-lg border border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-3">
            <KeyRound className="h-6 w-6 text-rose-600" />
          </div>
          <CardTitle className="text-slate-800 font-[Manrope] text-xl">
            Set your admin password
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            First-login onboarding. Your temporary password will stop working
            once you save a new one.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Temporary password
              </Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                data-testid="onboarding-current-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                New password
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                data-testid="onboarding-new-password"
              />
              <p className="text-[10.5px] text-slate-400 mt-1">
                Min 8 characters, letters + digits.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Confirm new password
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="onboarding-confirm-password"
              />
            </div>
            {err && (
              <div className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded p-2"
                   data-testid="onboarding-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy || !current || !next || !confirm}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="onboarding-submit"
            >
              {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>)
                    : (<><ShieldCheck className="h-4 w-4 mr-2" /> Save password</>)}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
